<script setup>
import { onMounted, reactive, ref } from 'vue';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import CustomSidebarMenusAPI from 'next/sidebar/customMenus/api';

const ROLES = [
  { value: 'administrator', label: 'Administrador' },
  { value: 'agent', label: 'Agente' },
];

const EMBED_TYPES = [
  { value: 'iframe', label: 'Embutido (iframe dentro do Chatwoot)' },
  { value: 'direct_link', label: 'Link direto (abre em nova aba)' },
];

const isLoading = ref(true);
const isSaving = ref(false);
const menus = ref([]);
const editingId = ref(null);

const emptyForm = () => ({
  label: '',
  icon: 'i-lucide-link',
  url: '',
  embed_type: 'iframe',
  visible_roles: ['administrator', 'agent'],
});

const form = reactive(emptyForm());

const load = async () => {
  isLoading.value = true;
  try {
    const { data } = await CustomSidebarMenusAPI.get();
    menus.value = data;
  } catch (error) {
    useAlert('Não foi possível carregar os menus customizados');
  } finally {
    isLoading.value = false;
  }
};

const resetForm = () => {
  editingId.value = null;
  Object.assign(form, emptyForm());
};

const startEdit = menu => {
  editingId.value = menu.id;
  Object.assign(form, {
    label: menu.label,
    icon: menu.icon,
    url: menu.url,
    embed_type: menu.embed_type,
    visible_roles: [...(menu.visible_roles || [])],
  });
};

const toggleRole = role => {
  form.visible_roles = form.visible_roles.includes(role)
    ? form.visible_roles.filter(r => r !== role)
    : [...form.visible_roles, role];
};

// Aceita tanto uma URL simples quanto um código de incorporação
// completo (ex: o snippet <iframe> que o Google Calendar/Agenda,
// YouTube, etc. fornecem em "Compartilhar > Incorporar"). Se detectar
// uma tag <iframe>, extrai só o valor de src= — é isso que
// efetivamente guardamos e usamos no nosso próprio <iframe>.
const extractUrlFromInput = input => {
  const trimmed = (input || '').trim();
  if (!trimmed.toLowerCase().includes('<iframe')) return trimmed;

  const match = trimmed.match(/src\s*=\s*["']([^"']+)["']/i);
  return match ? match[1] : trimmed;
};

const save = async () => {
  const url = extractUrlFromInput(form.url);

  if (!form.label || !url) {
    useAlert('Preencha ao menos o nome e a URL (ou cole o código de incorporação)');
    return;
  }

  isSaving.value = true;
  try {
    const payload = { ...form, url };
    if (editingId.value) {
      await CustomSidebarMenusAPI.update(editingId.value, payload);
      useAlert('Menu atualizado com sucesso');
    } else {
      await CustomSidebarMenusAPI.create(payload);
      useAlert('Menu criado com sucesso');
    }
    resetForm();
    await load();
  } catch (error) {
    useAlert('Não foi possível salvar o menu');
  } finally {
    isSaving.value = false;
  }
};

const remove = async menu => {
  try {
    await CustomSidebarMenusAPI.delete(menu.id);
    useAlert('Menu removido');
    if (editingId.value === menu.id) resetForm();
    await load();
  } catch (error) {
    useAlert('Não foi possível remover o menu');
  }
};

onMounted(load);
</script>

<template>
  <div class="flex flex-col w-full h-full min-h-0 overflow-y-auto bg-n-background p-6">
    <header class="flex-shrink-0 mb-6">
      <h1 class="text-base font-medium text-n-slate-12 m-0">Menus customizados</h1>
      <p class="text-sm text-n-slate-11 m-0 max-w-xl">
        Adicione itens novos na barra lateral que abrem uma URL externa —
        embutida num iframe dentro do Chatwoot, ou diretamente em nova aba.
      </p>
    </header>

    <div v-if="isLoading" class="flex justify-center py-10">
      <Spinner />
    </div>

    <div v-else class="flex flex-col gap-8 max-w-2xl">
      <table v-if="menus.length" class="w-full text-sm border-collapse">
        <thead>
          <tr class="border-b border-n-weak">
            <th class="text-left py-2 font-medium text-n-slate-11">Nome</th>
            <th class="text-left py-2 font-medium text-n-slate-11">Tipo</th>
            <th class="text-left py-2 font-medium text-n-slate-11">Visível para</th>
            <th class="text-right py-2 font-medium text-n-slate-11">Ações</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="menu in menus" :key="menu.id" class="border-b border-n-weak">
            <td class="py-2 text-n-slate-12">{{ menu.label }}</td>
            <td class="py-2 text-n-slate-11">
              {{ menu.embed_type === 'iframe' ? 'Embutido' : 'Link direto' }}
            </td>
            <td class="py-2 text-n-slate-11">
              {{ (menu.visible_roles || []).join(', ') || 'Ninguém' }}
            </td>
            <td class="py-2 text-right">
              <Button
                label="Editar"
                variant="ghost"
                size="xs"
                @click="startEdit(menu)"
              />
              <Button
                label="Excluir"
                variant="ghost"
                color="ruby"
                size="xs"
                @click="remove(menu)"
              />
            </td>
          </tr>
        </tbody>
      </table>

      <div class="flex flex-col gap-4 p-4 rounded-lg bg-n-alpha-1">
        <h2 class="text-sm font-medium text-n-slate-12 m-0">
          {{ editingId ? 'Editar menu' : 'Novo menu' }}
        </h2>

        <div class="flex flex-col gap-2">
          <label class="text-sm text-n-slate-11" for="menu-label">Nome (aparece na sidebar)</label>
          <input
            id="menu-label"
            v-model="form.label"
            type="text"
            placeholder="Ex: Prontuário"
            class="reset-base w-full h-9 px-3 rounded-lg border border-n-weak bg-n-solid-2 text-sm text-n-slate-12"
          />
        </div>

        <div class="flex flex-col gap-2">
          <label class="text-sm text-n-slate-11" for="menu-url">
            URL ou código de incorporação (embed)
          </label>
          <textarea
            id="menu-url"
            v-model="form.url"
            rows="3"
            placeholder="https://exemplo.com ou cole aqui um <iframe ...>...</iframe>"
            class="reset-base w-full px-3 py-2 rounded-lg border border-n-weak bg-n-solid-2 text-sm text-n-slate-12 font-mono"
          />
          <span class="text-xs text-n-slate-10">
            Pode colar direto o código de "Compartilhar → Incorporar" do
            Google Agenda, YouTube, etc. — a URL (src) é extraída
            automaticamente ao salvar.
          </span>
        </div>

        <div class="flex flex-col gap-2">
          <label class="text-sm text-n-slate-11" for="menu-icon">
            Ícone (nome de um ícone Lucide, ex: i-lucide-link)
          </label>
          <input
            id="menu-icon"
            v-model="form.icon"
            type="text"
            placeholder="i-lucide-link"
            class="reset-base w-full h-9 px-3 rounded-lg border border-n-weak bg-n-solid-2 text-sm text-n-slate-12"
          />
        </div>

        <div class="flex flex-col gap-2">
          <span class="text-sm text-n-slate-11">Como abrir</span>
          <label
            v-for="type in EMBED_TYPES"
            :key="type.value"
            class="flex items-center gap-2 text-sm text-n-slate-12"
          >
            <input
              v-model="form.embed_type"
              type="radio"
              :value="type.value"
              name="embed_type"
            />
            {{ type.label }}
          </label>
        </div>

        <div class="flex flex-col gap-2">
          <span class="text-sm text-n-slate-11">Visível para</span>
          <label
            v-for="role in ROLES"
            :key="role.value"
            class="flex items-center gap-2 text-sm text-n-slate-12"
          >
            <Checkbox
              :model-value="form.visible_roles.includes(role.value)"
              @change="() => toggleRole(role.value)"
            />
            {{ role.label }}
          </label>
        </div>

        <div class="flex items-center gap-2">
          <Button
            :label="editingId ? 'Salvar alterações' : 'Adicionar menu'"
            size="sm"
            :is-loading="isSaving"
            @click="save"
          />
          <Button
            v-if="editingId"
            label="Cancelar"
            variant="ghost"
            size="sm"
            @click="resetForm"
          />
        </div>
      </div>
    </div>
  </div>
</template>
